import { SkyDataGrid, SkyText, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import type { CSSProperties } from "react";
import { CHECK_KIND_LABELS, type MonitorCheckRow } from "@shared/types/monitor";
import { StatusCell } from "@/components/runs/StatusCell";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import { skipDetailText, valueAgainstLimit } from "@/lib/monitor/format";
import { MonitorStatusBadge } from "./MonitorStatusBadge";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Fixes wave 4b (post-release check 4, 03c-monitor-schedules.png, 1440 px): every column was a
 * percentage, and the two time headers ran together with no gap. Border-box widths measured in
 * Chromium (16 px semibold headers, the larger of the app's stack and Inter): the two times have
 * pixel widths that hold their headers and a 14 px display-zone time (round 3, 2026-09-30: "May
 * 28, 2026, 08:08 AM", 172 px, plus the cell's 24 px) on one line; the headers no longer carry
 * "(UTC)", the page names the zone once. The others keep at least their header's room, sort icon
 * included, and the table a floor of 952 px, below which it scrolls sideways.
 */
const LAST_RUN_WIDTH = 200;
const CHECKED_WIDTH = 200;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  scheduleName: 160,
  kind: 120,
  status: 120,
  lastValue: 152,
};

/**
 * The Schedules tab (spec section 11): each schedule's `overdue_run`, `duration` and (#829)
 * `failed_run` checks, how
 * late it is or how long its run has taken against the limit, and when it last started a run.
 */
export function MonitorSchedulesGrid({ rows }: { rows: MonitorCheckRow[] }) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<MonitorCheckRow>[] = [
    {
      key: "scheduleName",
      header: "Schedule",
      width: "24%",
      sortable: true,
      renderCell: (_value, row) => (
        <SkyText size={300} block truncate title={row.scheduleName ?? undefined}>
          {row.scheduleName ?? "Deleted schedule"}
        </SkyText>
      ),
    },
    {
      key: "kind",
      header: "Monitor",
      width: "16%",
      renderCell: (_value, row) => <SkyText size={300}>{CHECK_KIND_LABELS[row.kind]}</SkyText>,
    },
    {
      key: "status",
      header: "Status",
      width: "12%",
      sortable: true,
      // `StatusCell`: a badge straight in a cell draws as a bar across the column.
      renderCell: (_value, row) => (
        <StatusCell
          badge={
            <MonitorStatusBadge status={row.status} testId={`monitor-schedule-status-${row.id}`} />
          }
        />
      ),
    },
    {
      key: "lastValue",
      header: "Found / limit",
      width: "16%",
      renderCell: (_value, row) => {
        // Round 10 wave 2 (A2b): a breach by skipped firings says why, under the count.
        const why = skipDetailText(row);
        return (
          <div className="flex min-w-0 flex-col">
            <SkyText size={300} font="numeric">
              {valueAgainstLimit(row)}
            </SkyText>
            {why ? (
              <SkyText
                size={200}
                color="muted"
                block
                truncate
                title={why}
                testId={`monitor-schedule-skip-${row.id}`}
              >
                {why}
              </SkyText>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "signalAt",
      header: "Last run start",
      width: `${LAST_RUN_WIDTH}px`,
      renderCell: (_value, row) => (
        <SkyText size={300} font="numeric" title={instantHoverTitle(row.signalAt, zone)}>
          {row.signalAt ? formatInstant(row.signalAt, zone, "minutes") : "No run yet"}
        </SkyText>
      ),
    },
    {
      key: "lastEvalAt",
      header: "Checked",
      width: `${CHECKED_WIDTH}px`,
      renderCell: (_value, row) => (
        <SkyText size={300} font="numeric" title={instantHoverTitle(row.lastEvalAt, zone)}>
          {row.lastEvalAt ? formatInstant(row.lastEvalAt, zone, "minutes") : "Not yet"}
        </SkyText>
      ),
    },
  ];
  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  return (
    <div style={{ "--monitor-schedules-floor": `${layout.floor}px` } as CSSProperties}>
      <SkyDataGrid<MonitorCheckRow>
        data={rows}
        columns={layout.columns}
        getRowId={(row) => row.id}
        {...LIST_GRID_PAGINATION}
        className="[&_table]:min-w-(--monitor-schedules-floor)"
        ariaLabel="Watched schedules"
        emptyMessage="No schedule is watched yet: every enabled schedule is watched once it exists."
        testId="monitor-schedules-grid"
      />
    </div>
  );
}
